import { useEffect } from 'react';
import { trackEvent } from '../lib/analytics.js';

export default function Guide({
  isGuideOpen,
  setIsGuideOpen,
  guideStepIndex,
  setGuideStepIndex,
  demoSteps,
  currentGuideStep,
  nextGuideStep,
  activeTab,
  sinkViewMode,
  isLargeScreen,
}) {
  useEffect(() => {
    if (isGuideOpen && currentGuideStep) {
      if (guideStepIndex === 0) {
        trackEvent('tutorial_begin', { surface: 'demo' });
      }
      trackEvent('tutorial_step_view', { surface: 'demo', step: currentGuideStep.id });
    }
  }, [isGuideOpen, guideStepIndex, currentGuideStep]);

  if (!isGuideOpen || !currentGuideStep) return null;

  const handleClose = () => {
    trackEvent('tutorial_step_click', { surface: 'demo', step: currentGuideStep.id, direction: 'close' });
    setIsGuideOpen(false);
  };

  const handleExitToLp = () => {
    trackEvent('demo_exit_to_lp', { surface: 'demo' });
  };

  const handleInviteClick = () => {
    trackEvent('demo_invite_registration_click', { surface: 'demo', placement: 'guide_final' });
  };

  const handlePrev = () => {
    const prevIndex = Math.max(guideStepIndex - 1, 0);
    trackEvent('tutorial_step_click', { surface: 'demo', step: currentGuideStep.id, direction: 'prev' });
    setGuideStepIndex(prevIndex);
    demoSteps[prevIndex]?.action();
  };

  const handleNext = () => {
    trackEvent('tutorial_step_click', { surface: 'demo', step: currentGuideStep.id, direction: 'next' });
    if (guideStepIndex === demoSteps.length - 1) {
      trackEvent('tutorial_complete', { surface: 'demo' });
      setIsGuideOpen(false);
      return;
    }
    const nextIndex = Math.min(guideStepIndex + 1, demoSteps.length - 1);
    setGuideStepIndex(nextIndex);
    demoSteps[nextIndex]?.action();
  };

  return (
    <aside className={`fixed left-3 right-3 ${activeTab === 'input' && sinkViewMode === 'input' ? 'bottom-[180px]' : 'bottom-[72px]'} z-[150] 
      ${isLargeScreen ? (
        guideStepIndex === 0 ? 'sm:left-6 sm:right-auto sm:top-1/2 sm:-translate-y-1/2 sm:bottom-auto' :
        guideStepIndex === 1 ? 'sm:left-1/2 sm:-translate-x-1/2 sm:bottom-12 sm:top-auto sm:translate-y-0' :
        'sm:left-1/2 sm:-translate-x-1/2 sm:top-16 sm:bottom-auto sm:translate-y-0'
      ) : ''} 
      sm:w-[400px] rounded-2xl border-2 border-indigo-400/50 bg-indigo-950/98 shadow-[0_0_40px_-10px_rgba(99,102,241,0.3)] backdrop-blur-md p-5 transition-all duration-500 ease-in-out`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-400">Step {guideStepIndex + 1} / {demoSteps.length}</p>
          <h2 className="text-base font-black text-white uppercase tracking-tight">{currentGuideStep.guideTitle}</h2>
        </div>
        <button aria-label="閉じる" onClick={handleClose} className="text-indigo-400 hover:text-white text-xl leading-none transition-colors p-1">
          ×
        </button>
      </div>
      <p className="mt-3 text-[13px] leading-relaxed text-indigo-100/90 font-medium">{currentGuideStep.guideBody}</p>
      {currentGuideStep.guideChecklist?.length > 0 && (
        <div className="mt-4 rounded-xl border border-indigo-400/20 bg-indigo-900/30 p-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-indigo-300 mb-2">このアクションでやること</p>
          <ul className="space-y-2 text-[12px] text-indigo-50/90 leading-tight">
            {currentGuideStep.guideChecklist.map((item) => (
              <li key={item} className="flex gap-2.5 items-start">
                <span className="text-indigo-400 mt-0.5">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="mt-3 flex items-center justify-between gap-2">
        <a
          href="/about"
          onClick={handleExitToLp}
          className="rounded border border-gray-700 px-3 py-2 text-xs font-bold text-gray-300 hover:bg-gray-800"
        >
          LPに戻る
        </a>
        <div className="flex items-center gap-2">
          {guideStepIndex === demoSteps.length - 1 && (
            <a
              href="/app"
              onClick={handleInviteClick}
              className="rounded bg-emerald-600 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-500"
            >
              招待リストに登録
            </a>
          )}
          <button
            onClick={handlePrev}
            disabled={guideStepIndex === 0}
            className="rounded border border-gray-700 px-3 py-2 text-xs font-bold text-gray-300 hover:bg-gray-800 disabled:text-gray-600 disabled:hover:bg-transparent"
          >
            戻る
          </button>
          <button
            onClick={handleNext}
            className="rounded bg-blue-600 px-3 py-2 text-xs font-bold text-white hover:bg-blue-500"
          >
            {guideStepIndex === demoSteps.length - 1 ? '閉じる' : `Step ${guideStepIndex + 2}: ${nextGuideStep.label}`}
          </button>
        </div>
      </div>
    </aside>
  );
}
